import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchIcd10 } from '../../api/catalogues.js'
import { useDebounce } from '../../hooks/useDebounce.js'

// Live ICD-10 search. Types into the input, debounces 200ms, hits
// /api/catalogues/icd10/?q=… and renders a dropdown of { code, description }.
// Clicking / tapping a row calls onPick({ code, description }) and clears the input.
// Mobile: onMouseDown handler fires before onBlur so taps don't dismiss the menu.
export default function ICD10Autocomplete({ onPick, excludeCodes = [], placeholder, autoFocus }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const debounced = useDebounce(q, 200)

  const { data = [], isFetching } = useQuery({
    queryKey: ['icd10', debounced],
    queryFn: () => searchIcd10(debounced),
    enabled: debounced.trim().length >= 2,
    // Cache recent searches for 5 minutes — popular codes reuse the same URL.
    staleTime: 5 * 60_000,
  })

  const filtered = data.filter((c) => !excludeCodes.includes(c.code))

  function pick(c) {
    // Forward the full shape including `id` — the encounter write path
    // (syncIcd10Diagnoses) diffs by id to know what to POST.
    onPick({ id: c.id, code: c.code, description: c.description, title: c.title })
    setQ('')
    setOpen(false)
    setHighlight(0)
    // keep focus for rapid-fire coding
    inputRef.current?.focus()
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((i) => Math.min(i + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  const showMenu = open && debounced.trim().length >= 2

  return (
    <div className="relative">
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Type 2+ characters to search ICD-10…'}
        className="w-full border rounded px-3 py-2 text-sm"
        inputMode="search"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showMenu}
      />
      {showMenu && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto text-sm">
          {isFetching && <div className="p-2 text-slate-400">Searching…</div>}
          {!isFetching && filtered.length === 0 && (
            <div className="p-2 text-slate-400">No matching codes</div>
          )}
          {filtered.map((c, i) => (
            <button
              type="button"
              key={c.code}
              onMouseDown={(e) => { e.preventDefault(); pick(c) }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 ${i === highlight ? 'bg-wantok-50' : 'hover:bg-slate-50'}`}
            >
              <span className="font-mono text-xs text-wantok-700">{c.code}</span>
              <span className="ml-2">{c.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
